import type { ColorMode } from '@chukua/common';
import { Map as MapLibreMap, type GeoJSONSource } from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';
import { configureMapLibreWorker } from '../../../shared/features/maps/adapters/configure-maplibre-worker';
import { basemapStyleUrl } from '../../../shared/features/maps/policies/basemap';
import { CORRIDOR_CENTER, CORRIDOR_ZOOM, ZONE_FIT_OPTIONS } from '../../../shared/features/maps/policies/map-framing';
import { useZonesOverlay } from '../../zones/public/use-zones-overlay';
import { TRIP_TRACK_LINE_LAYER_ID, TRIP_TRACK_SOURCE_ID, tripTrackLayers } from '../adapters/trip-track-layers';
import { trackBounds, trackFeatures } from '../policies/trip-track-geojson.policy';
import type { TripStop } from '../policies/trip-stops.policy';
import type { TrackPoint } from '../trips.api';

interface UseTripMapArgs {
  /** Null while unknown or unset: no map is mounted. */
  tileKey: string | null;
  mode: ColorMode;
  points: TrackPoint[];
  stops: TripStop[];
  /** A place to centre on; a new object centres again, so choosing the same stop twice still moves the map back to it. */
  focus: { lng: number; lat: number } | null;
}

function mountTripMap(container: HTMLDivElement, tileKey: string, mode: ColorMode, onLoad: (map: MapLibreMap) => void) {
  configureMapLibreWorker();
  const map = new MapLibreMap({ container, style: basemapStyleUrl(mode, tileKey), center: CORRIDOR_CENTER, zoom: CORRIDOR_ZOOM });
  map.on('load', () => {
    map.addSource(TRIP_TRACK_SOURCE_ID, { type: 'geojson', data: trackFeatures([]) });
    tripTrackLayers(mode).forEach((layer) => map.addLayer(layer));
    onLoad(map);
  });
  const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => map.resize());
  resizeObserver?.observe(container);
  return () => {
    resizeObserver?.disconnect();
    map.remove();
  };
}

/**
 * The trip's map: the basemap, the company's zones under the route, the route and the truck. The view frames the track once, the
 * first time it has a fix, so a later pan or zoom by the person is never undone by a live fix.
 */
export function useTripMap({ tileKey, mode, points, stops, focus }: UseTripMapArgs) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [map, setMap] = useState<MapLibreMap | null>(null);
  const hasFramedRef = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !tileKey) return;
    // A new map (another colour mode, another trip) starts at the default view and must be framed again.
    hasFramedRef.current = false;
    const destroy = mountTripMap(container, tileKey, mode, setMap);
    return () => {
      setMap(null);
      destroy();
    };
  }, [tileKey, mode]);

  useZonesOverlay(map, mode, TRIP_TRACK_LINE_LAYER_ID);

  useEffect(() => {
    map?.getSource<GeoJSONSource>(TRIP_TRACK_SOURCE_ID)?.setData(trackFeatures(points, stops));
  }, [map, points, stops]);

  useEffect(() => {
    if (map && focus) map.flyTo({ center: [focus.lng, focus.lat], zoom: Math.max(map.getZoom(), 12) });
  }, [map, focus]);

  useEffect(() => {
    const bounds = trackBounds(points);
    if (!map || hasFramedRef.current || !bounds) return;
    hasFramedRef.current = true;
    map.fitBounds(bounds, { ...ZONE_FIT_OPTIONS, maxZoom: 14, duration: 0 });
  }, [map, points]);

  return { containerRef };
}
